import type { RootState } from '@appStore/store.ts'
import type { LegacyAction } from 'actions/mapping.typed.ts'
import type { NavigateFunction } from 'react-router-dom'

import { campaignCreate } from 'actions/campaignCreate'
import ArrowIcon from 'assets/image/icons/svg/icons/Arrow-down.svg'
import deleteIcon from 'assets/image/icons/svg/icons/delete-icon.svg'
import clsx from 'clsx'
import { CONSTS } from 'config/objectConst'
import { campaignsSelectors } from 'features/campaigns'
import { PostReaction } from 'features/executions/types.ts'
import { notificationUtils } from 'features/notifications'
import { Component, createRef, type RefObject } from 'react'
import { connect } from 'react-redux'
import { Element, scroller } from 'react-scroll'
import { notifyError } from 'shared/errors/notificate'
import { LoadingButton } from 'shared/ui/index.ts'
import { functions } from 'tools/functions'

import ErrorHelper from '../../atoms/ErrorHelper'
import HintTooltip from '../../atoms/HintTooltip'
import { FormField } from '../../molecules/formField'
import ModalWindow from '../../molecules/ModalWindow'
import SelectContainer from '../../molecules/selectContainer'
import { ActionSelect } from './ActionSelect'
import { Cancel } from './Cancel'
import { defaultNewAction } from './const'
import css from './style.module.scss'
import TimerComponent from './timer'
import { typeToCN } from './utils'
import { type Action, getActions } from './utils/actions'

const fillInWithData = (from: any) =>
	from.map((item: any) => ({
		delay: item.values.delay,
		period: item.values.period,
		textarea: item.values.textarea,
		type: item.type
	}))

const objectsAreSame = (x: any, y: any) => {
	x ??= {}
	y ??= {}

	for (const key in x) {
		if (x[key] !== y[key]) {
			return false
		}
	}

	return true
}

export class ContainerStep extends Component<
	ReturnType<typeof mapStateToProps> &
		ReturnType<typeof mapStateToProps> & {
			navigate: NavigateFunction
		},
	{
		actionId: null | string
		actions: Action[]
		actionsList: LegacyAction[]
		actionsListInitial: LegacyAction[]
		campaignId: null | string
		chooseActionMessageOpen: boolean
		isConnectDelay: boolean | null
		isDataChanged: boolean
		modalDeleteValue: null | string
		saveDisabledController: boolean
		scrollBarWidth: boolean
	}
> {
    wrapper: RefObject<HTMLPreElement>

	get isChanged() {
		const isSameSize =
			this.state.actionsList.length === this.state.actionsListInitial.length

		return !(isSameSize && this.isEqualValues())
	}

	get isConnectDelayCorrect() {
		const connect = this.findByName(CONSTS.AVAILABLE_ACTIONS.Connect.Title)
		const delay = connect?.values?.delay

		return Boolean(
			delay &&
				delay >=
					CONSTS.DEFAULT_DATE_ACTIONS_VALUE.CONNECT.DELAY.MIN_VALUE_MINUTES
		)
	}

	get isWithdrawValidationError() {
		const connect = this.findByName(CONSTS.AVAILABLE_ACTIONS.Connect.Title)
		const period = connect?.values?.period
		if (period === undefined || period === null) {
			return false
		}
		const days = Math.floor(Number(period) / 60 / 24)
		return (
			days <
				CONSTS.DEFAULT_DATE_ACTIONS_VALUE.CONNECT.WITHDRAW.MIN_VALUE_DAYS
		)
	}

	constructor(props: any) {
		super(props)

		this.state = {
			actionId: null,
			actions: getActions(),
			actionsList: [],
			actionsListInitial: [],
			campaignId: null,
			chooseActionMessageOpen: false,
			isConnectDelay: null,
			isDataChanged: false,

			modalDeleteValue: null,
			saveDisabledController: false,
			scrollBarWidth: false
		}
        this.wrapper = createRef<HTMLPreElement>()
	}

	accordionClick = (item: LegacyAction) => {
		const foundIndex = this.state.actionsList.findIndex(el => el.id === item.id)
		const action = this.state.actionsList[foundIndex]

		const newActionsList = this.state.actionsList.with(foundIndex, {
			...action,
			open: !item.open
		})

		this.setState({
			actionsList: newActionsList
		})
	}

	accordionGeneratorLabelText = (
		item: LegacyAction,
		index: number,
		arrow: boolean
	) => {
		if (item.name === CONSTS.AVAILABLE_ACTIONS.Message.Title && index > 0) {
			return (
				<p className={arrow ? 'action-label' : 'action-label-with-out-cursor'}>
					Action {index + 1}: <span>Follow up message</span>
				</p>
			)
		}
		return (
			<p className={arrow ? 'action-label' : 'action-label-with-out-cursor'}>
				Action {index + 1}:<span>{item.name}</span>
			</p>
		)
	}

	checkMessage = (message: string) => {
		const fields = [...message.matchAll(/{(?<customField>.*?)}/gim)]

		const customFields = [
			...CONSTS.MAIN_CUSTOM_FIELDS,
			...CONSTS.OVERFLOW_CUSTOM_FIELDS
		]

		return fields.filter(el => !customFields.some(rule => rule === el[0]))
	}

	componentDidMount() {
		window.addEventListener('resize', this.handleResize)
		if (this.props.scrollToAction) {
			setTimeout(() => {
				scroller.scrollTo(`myScrollToElement-${this.props.scrollToAction}`, {
					containerId: 'homeTemplateContainerID',
					delay: 0,
					duration: 150,
					offset: 250,
					smooth: false
				})
				campaignCreate.editScrollEvent(false)
			}, 0)
		}

		if (this.props.campaign.id !== null) {
			this.setState({
				actionsList: this.props.campaign.actions,
				campaignId: this.props.campaign.id
			})
		}
	}

	componentDidUpdate(prevProps: any) {
		if (
			this.props.campaign.id &&
			prevProps.campaign.id !== this.props.campaign.id
		) {
			this.setState({
				actionsList: this.props.campaign.actions,
				actionsListInitial: fillInWithData(this.props.campaign.actions)
			})
		}
		this.handleResize()
	}

	componentWillUnmount() {
		campaignCreate.zoomMessageWindow('')
		window.removeEventListener('resize', this.handleResize)
	}

	correctDelayOnSave() {
		const actions = this.state.actionsList
		if (actions.length === 1) {
			const action = actions[0]

			if (action.name === CONSTS.AVAILABLE_ACTIONS.Connect.Title) {
				action.values.delay = 0
			}
		}

		return actions
	}

	deleteAction = (id: null | string) => {
		try {
			const index = this.state.actionsList.findIndex(item => item.index === id)
			if (index === -1) {
				return
			}
			const newActionsList = this.state.actionsList.slice(0, index)
			this.setState(
				{ actionsList: newActionsList, isConnectDelay: false, modalDeleteValue: null },
				() => notificationUtils.snackById('3.3')
			)
		} catch (error) {
			notifyError(error)
		}
	}

	findByName = (name: string) =>
		this.state.actionsList.find(item => item.name === name)

	findIndexActionsWithError = (list: LegacyAction[]) => {
		const result = list.filter(
			el =>
				!el.validation.textarea &&
				el.type === CONSTS.AVAILABLE_ACTIONS.Message.Type
		)

		/* TODO if find try, scroll to element and open acardion */

		if (result.length > 0) {
			for (const element of result) {
				element.open = true
			}

			this.setState({ actionsList: this.state.actionsList })

			setTimeout(() => {
				scroller.scrollTo(`myScrollToElement-${result[0].index}`, {
					containerId: 'homeTemplateContainerID',
					delay: 0,
					duration: 500,
					offset: -25,
					smooth: true
				})
			}, 0)
		}
	}

	getDelayDescription = (item: LegacyAction) => {
		switch (item.type) {
			case CONSTS.AVAILABLE_ACTIONS.Connect.Type:
				return ['Once connection is', <br key='🌌' />, 'accepted, wait']
			case CONSTS.AVAILABLE_ACTIONS.Message.Type:
				return ['If no response in']
			default:
				return ['After execution']
		}
	}

	handleResize = () => {
		if (this.wrapper.current) {
			const scrollbarWidth =
				this.wrapper.current.offsetWidth - this.wrapper.current.clientWidth

			if (scrollbarWidth > 0 && !this.state.scrollBarWidth) {
				this.setState({ scrollBarWidth: true })
			}

			if (scrollbarWidth === 0 && this.state.scrollBarWidth) {
				this.setState({ scrollBarWidth: false })
			}
		}
	}

	handleSelect = (action: Action) => {
		const choseObject = action
		const choseObjectParams = { ...choseObject.params }
		const newChoseObject = { ...choseObject, params: choseObjectParams }

		const id = crypto.randomUUID()

		const buildObject = {
			id,
			index: id,
			...defaultNewAction,
			...newChoseObject,
			values: {
				...defaultNewAction.values,
				...newChoseObject.values,
				period: action.name === CONSTS.AVAILABLE_ACTIONS.Connect.Title
					? CONSTS.DEFAULT_DATE_ACTIONS_VALUE.CONNECT.WITHDRAW.DEFAULT * 1440
					: CONSTS.DEFAULT_DATE_ACTIONS_VALUE.MESSAGE_TIMEOUTS_DAYS[
						this.state.actionsList.length
					] * 1440
			}
		} as unknown as LegacyAction

		setTimeout(() => {
			scroller.scrollTo(`myScrollToElement-${buildObject.index}`, {
				containerId: 'homeTemplateContainerID',
				delay: 0,
				duration: 150,
				offset: -25,
				smooth: false
			})
		}, 0)

		if (action.name === CONSTS.AVAILABLE_ACTIONS.Message.Title) {
			this.setState(state => ({
				...state,
				actionsList: state.actionsList.map(item => {
					item.open = false
					return item
				})
			}))
		}

		if (
			this.state.actionsList.length <
			CONSTS.DEFAULT_DATE_ACTIONS_VALUE.MESSAGE_TIMEOUTS_DAYS.length
		) {
			this.setState({ actionsList: [...this.state.actionsList, buildObject] })
		}
	}

	isEqualValues = () => {
		let isEqual = true
		const values = fillInWithData(this.state.actionsList)
		values.forEach((item: typeof values[number], index: number) => {
			if (!objectsAreSame(item, this.state.actionsListInitial[index])) {
				return (isEqual = false)
			}
		})
		return isEqual
	}

	isError = () =>
		this.state.actionsList.map(el => this.checkMessage(el.values.textarea))

	render() {
		const actionNumber = this.state.actionsList.length

		const lastAction = this.state.actionsList[this.state.actionsList.length - 1]

		const isLastMessage = Boolean(
			lastAction && lastAction.name === CONSTS.AVAILABLE_ACTIONS.Message.Title
		)

		return (
			<div className={clsx('form-step-wrapper', css.root)}>
				<div className='hidden-step-line' />
				<div className='form-step'>
					<div
						className={`form-step-actions multiple-actions ${
							this.props.zoomMessageWindow ? 'hidden-block' : ''
						}`}
					>
						{this.state.actionsList.map((item, index) => (
							<Element
								className='action-group-wrap'
								key={item.index}
								name={`myScrollToElement-${item.index}`}
							>
								<div
									className={clsx(
										typeToCN[item.type],
										item.open && isLastMessage
											? 'action-group-accardion-open open'
											: lastAction &&
													lastAction.name ===
														CONSTS.AVAILABLE_ACTIONS.Connect.Title
												? 'action-group-accardion-connect'
												: item.open &&
														lastAction &&
														lastAction.name ===
															CONSTS.AVAILABLE_ACTIONS.ReactOnPost.Title
													? 'action-group-accardion-react open'
													: 'action-group-accardion'
									)}
								>
									<div
										className='action-label-wrap'
										onClick={() => item.arrow && this.accordionClick(item)}
									>
										{item.arrow && (
											<img alt='arrow' className='icon-arrow' src={ArrowIcon} />
										)}
										<div className='text-field-label'>
											{this.accordionGeneratorLabelText(
												item,
												index,
												item.arrow
											)}
										</div>
									</div>

									{this.props.campaign.allowAddSteps && (
										<div
											className='delete-action'
											onClick={() => {
												this.setState({
													modalDeleteValue: item.index
												})
											}}
										>
											<img
												alt='delete action'
												className='delete-action__icon'
												src={deleteIcon}
											/>
											<div className='delete-action__text'>Delete</div>
										</div>
									)}
								</div>
								<div
									className={item.open ? 'action-group open' : 'action-group'}
								>
									<SelectContainer
										checkMessage={this.checkMessage}
										index={index}
										item={item}
										saveReaction={this.saveReaction}
										setActionId={(id: string) =>
											this.setState({ actionId: id })
										}
										textAreaLengthValidation={this.textAreaLengthValidation}
										timerChanger={this.timerChanger}
										validationLaunchActionIn={this.validationLaunchActionIn}
										validationMessage={this.validationMessage}
										validationPeriod={this.validationPeriod}
									/>
								</div>
								{(item.name !== CONSTS.AVAILABLE_ACTIONS.Connect.Title ||
									lastAction?.name !==
										CONSTS.AVAILABLE_ACTIONS.Connect.Title) && (
									<FormField
										beforeLabel={this.getDelayDescription(item)}
										className='short-label delay-field bottom-timer'
										label='Delay'
									>
										<TimerComponent
											className='timer'
											item={item}
											showController={true}
											time={
												item.name === CONSTS.AVAILABLE_ACTIONS.Connect.Type
													? item.values.delay
													: item.values.period
											}
											timerChanger={this.timerChanger}
										/>
										{item.name === CONSTS.AVAILABLE_ACTIONS.Connect.Title &&
											!this.isConnectDelayCorrect &&
											this.state.isConnectDelay && (
												<div className='error-container-accent'>
													<ErrorHelper>
														{` Must be at least ${
															CONSTS.DEFAULT_DATE_ACTIONS_VALUE.CONNECT.DELAY
																.MIN_VALUE_MINUTES
														} minutes`}
													</ErrorHelper>
												</div>
											)}
									</FormField>
								)}
							</Element>
						))}
					</div>
					{!this.props.zoomMessageWindow && (
						<div
							className={clsx(
								isLastMessage ? 'event-action-add-open' : 'event-action-add',
								actionNumber ? 'no-border' : 'short'
							)}
						>
							{this.props.campaign.allowAddSteps &&
								actionNumber <
									CONSTS.DEFAULT_DATE_ACTIONS_VALUE.MESSAGE_TIMEOUTS_DAYS
										.length &&
								this.renderMainActionField()}

							<div className='buttons-group'>
								<Cancel isChanged={this.isChanged} />

								<LoadingButton
									disabled={
										actionNumber === 0 &&
										this.saveDisabledController(this.state.actionsList)
									}
									isLoading={this.props.updateCampaignController}
									onClick={this.setFieldsData}
								>
									Save
								</LoadingButton>
								{((!this.isConnectDelayCorrect &&
									this.state.isConnectDelay &&
									this.setOpenConnect()) ||
									(!this.isConnectDelayCorrect &&
										this.state.isConnectDelay &&
										actionNumber > 1) ||
									!this.saveDisabledController(this.state.actionsList) ||
									this.isWithdrawValidationError ||
									this.isError()?.some(el => el.length !== 0)) && (
									<div className='error-container'>
										<ErrorHelper>Please fix all warnings</ErrorHelper>
									</div>
								)}
							</div>
						</div>
					)}
					{!!this.props.zoomMessageWindow && (
						<div className='zoomed-message'>
							<div
								className='change-size collapse'
								onClick={() => {
									campaignCreate.zoomMessageWindow('')
									this.scrollToElement(this.state.actionId)
								}}
							>
								&nbsp;
							</div>
							<div
								className={`text ${
									this.state.scrollBarWidth ? 'text-with-scroll' : ''
								}`}
							>
								<pre className='word-break' ref={this.wrapper}>
									{this.props.zoomMessageWindow}
								</pre>
							</div>
						</div>
					)}

					<ModalWindow
						closeHandler={() => this.setState({ modalDeleteValue: null })}
						closeText='No'
						confirmHandler={() =>
							this.deleteAction(this.state.modalDeleteValue)
						}
						confirmText='Yes'
						open={!!this.state.modalDeleteValue}
						{...CONSTS.confirmationDialogs[1.4]}
					/>
				</div>
			</div>
		)
	}

	renderMainActionField = () => {
		return (
			<div className='hint-tooltip-wrapper'>
				<FormField
					className={this.state.actionsList.length === 0 ? 'icon' : ''}
				>
					<ActionSelect
						actions={this.state.actions}
						actionsList={this.state.actionsList}
						onChange={this.handleSelect}
					/>
					<HintTooltip
						classes='first-action-tooltip'
						enabled={
							functions.isValidOnBoardingConditions(
								this.props.appProfile,
								'2.3'
							) &&
							!this.state.actionsList.length &&
							!window.localStorage.getItem('2.3')
						}
						handlerClose={() => {
							window.localStorage.setItem('2.3', 'true')
						}}
						placement='bottom-start'
						tooltip={CONSTS.ON_BOARDING['2.3']}
					/>
				</FormField>
			</div>
		)
	}

	save = () => {
		const actions = this.correctDelayOnSave()
		const url = `/campaigns/${this.props.campaign.id}`

		campaignCreate.save(actions, this.props.campaign.id, () => {
			if (this.props.campaign.allowAddSteps) {
				return this.props.navigate(`${url}/contacts`, {
					state: {
						launchCampaignDialog: true
					}
				})
			}

			return this.props.navigate(url)
		})
	}

	saveDisabledController = (array: LegacyAction[]) => {
		if (array.length === 0) {
			return true
		}

		return array.every(item => {
			const values = new Set(Object.values(item.validation))
			return !values.has(false)
		})
	}

	saveReaction = (value: boolean | PostReaction, index: string) => {
		const newActionsList = this.state.actionsList.map(item => {
			if (item.index === index) {
				if (item.type === CONSTS.AVAILABLE_ACTIONS.ReactOnPost.Type) {
					// @ts-ignore
					item.values.currentEmotion = value
				}
			}
			return item
		})
		this.setState({
			actionsList: newActionsList
		})
	}

	scrollToElement = (id: null | string) => {
		setTimeout(() => {
			scroller.scrollTo(`myScrollToElement-Action_${id}`, {
				containerId: 'homeTemplateContainerID',
				delay: 0,
				duration: 150,
				smooth: false
			})
		}, 0)
	}

	setFieldsData = () => {
		this.setState({ isConnectDelay: true })

		this.showErrorMessages(() => {
			this.showErrorMessageCallback()
			this.findIndexActionsWithError(this.state.actionsList)
			if (this.isWithdrawValidationError) {
				const connect = this.findByName(CONSTS.AVAILABLE_ACTIONS.Connect.Title)
				if (connect) {
					connect.open = true
					this.setState({ actionsList: this.state.actionsList })
					setTimeout(() => {
						scroller.scrollTo(`myScrollToElement-${connect.index}` as string, {
							containerId: 'homeTemplateContainerID',
							delay: 0,
							duration: 500,
							offset: -25,
							smooth: true
						})
					}, 0)
				}
			}
		})
	}

	setOpenConnect = () => {
		setTimeout(() => {
			scroller.scrollTo('myScrollToElement-Action_1', {
				containerId: 'homeTemplateContainerID',
				delay: 0,
				duration: 500,
				offset: -25,
				smooth: true
			})
		}, 0)
	}

	showErrorMessageCallback = () => {
		if (!this.saveDisabledController(this.state.actionsList)) {
			return
		}

		const isNoDelay = () => {
			const connect = this.findByName(CONSTS.AVAILABLE_ACTIONS.Connect.Title)
			const delay = connect?.values?.delay

			return delay === undefined
		}

		const isValid =
			!this.textAreaLengthValidation() &&
			this.isConnectDelayCorrect &&
			!this.isWithdrawValidationError

		if (isValid || isNoDelay()) {
			this.setState({ isConnectDelay: false })
			const hasError = this.isError()?.some(el => el.length !== 0)

			!hasError && this.save()
		}
	}

	showErrorMessages = (callback?: () => void) => {
		const newActionsList = this.state.actionsList.map(item => {
			if (
				(Object.prototype.hasOwnProperty.call(item.validation, 'textarea') &&
					item.validation.textarea &&
					item.values.textarea.length === 0 &&
					item.type === CONSTS.AVAILABLE_ACTIONS.Message.Type) ||
				(Object.prototype.hasOwnProperty.call(item.validation, 'textarea') &&
					item.validation.textarea &&
					this.checkMessage(item.values.textarea).length !== 0)
			) {
				item.validation.textarea = false
			}
			return item
		})
		this.setState(
			{ actionsList: newActionsList },
			callback ? () => callback() : undefined
		)
	}

	textAreaLengthValidation = () => {
		return this.state.actionsList.some(
			item =>
				item.values.textarea.length >
				(item.type === CONSTS.AVAILABLE_ACTIONS.Connect.Type
					? CONSTS.AVAILABLE_ACTIONS.Connect.MaxLength
					: CONSTS.AVAILABLE_ACTIONS.Message.MaxLength)
		)
	}

	timerChanger = (item: { index: string; name: string }, result: number, unique: any) => {
		const nowObject = this.state.actionsList.find(
			action => action.index === item.index
		)
		if (!unique) {
			this.state.isConnectDelay && this.setState({ isConnectDelay: false })
			if (item.name === CONSTS.AVAILABLE_ACTIONS.Connect.Title) {
				if (nowObject?.values.delay !== result) {
					const findInd = this.state.actionsList.findIndex(
						el => el.name === CONSTS.AVAILABLE_ACTIONS.Connect.Title
					)

					// eslint-disable-next-line react/no-direct-mutation-state
					this.state.actionsList[findInd].values.delay = result
				}
			} else {
				if (nowObject && nowObject?.values.delay !== result) {
					nowObject.values.period = result

					return nowObject
				}
			}
		} else {
			const findEl = this.state.actionsList.filter(
				el => el.name === CONSTS.AVAILABLE_ACTIONS.Connect.Title
			)
			const findInd = this.state.actionsList.indexOf(findEl[0])
			// eslint-disable-next-line react/no-direct-mutation-state
			this.state.actionsList[findInd].values.period = result
		}
	}

	validationLaunchActionIn = (value: number, index: string) => {
		const newActionsList = this.state.actionsList.map(item => {
			if (item.index === index) {
				item.values.launchActionIn = value
			}
			return item
		})
		this.setState({
			actionsList: newActionsList
		})
	}

	// validation message
	validationMessage = (value: string, index: string) => {
		const newActionsList = this.state.actionsList.map(item => {
			const maxLength =
				item.type === CONSTS.AVAILABLE_ACTIONS.Connect.Type
					? CONSTS.AVAILABLE_ACTIONS.Connect.MaxLength
					: CONSTS.AVAILABLE_ACTIONS.Message.MaxLength

			if (item.index === index) {
				if (item.type === CONSTS.AVAILABLE_ACTIONS.Connect.Type) {
					item.validation.textarea = maxLength >= value.length
				}
				if (item.type === CONSTS.AVAILABLE_ACTIONS.Message.Type) {
					item.validation.textarea =
						value.length > 0 && maxLength >= value.length
				}

				item.values.textarea = value
			}
			return item
		})
		this.setState({
			actionsList: newActionsList
		})
	}

	validationPeriod = (value: number, index: string) => {
		const newActionsList = this.state.actionsList.map(item => {
			if (item.index === index) {
				item.values.period = value
			}
			return item
		})
		this.setState({
			actionsList: newActionsList
		})
	}
}

const mapStateToProps = (state: RootState) => ({
	appProfile: state.app.appProfile,
	campaign: state.campaign,
	scrollToAction: campaignsSelectors.scrollToAction(state),
	updateCampaignController: state.campaign.updateCampaignController,
	zoomMessageWindow: state.campaignCreate.zoomMessageWindow
})

export default connect(mapStateToProps)(ContainerStep)
